import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "../../../locales";
import type {
  CanvasSettings,
  EditorElement,
  DragDropAnswerConfig,
  MatchingAnswerConfig,
  PaintingAnswerConfig,
  AnswerKeyValue,
} from "../../../editor/types";
import type { ChoiceOption, OrderListItem } from "../types";
import { MathMLContent } from "./MathMLContent";
import {
  resolvePaintSwatchAppearance,
  resolvePaintAreaAppearance,
} from "../lib/appearance";
import {
  getPaintShapePathD,
  PAINT_SHAPE_PICKER_OPTIONS,
  type PaintPoint,
} from "../lib/paintShapes";

type PaintShapeType = ReturnType<typeof resolvePaintAreaAppearance>["shapeType"];

// Free-polygon paint areas store their vertices on element.data.points.
const getPaintElementPoints = (
  element: EditorElement
): PaintPoint[] | undefined => {
  const pts = (element.data as { points?: PaintPoint[] })?.points;
  return Array.isArray(pts) ? pts : undefined;
};

type Matrix = {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
};

type Bounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type PaintAreaShape = {
  id: string;
  path: Path2D;
  bounds: Bounds;
};

const PAINT_INTERSECTION_PREFIX = "paint-intersection:";
const PAINT_SHAPE_INSET = 2;
const PAINT_INTERSECTION_SAMPLE_STEP = 12;
const PAINT_INTERSECTION_MIN_SAMPLES = 3;
const PAINT_INTERSECTION_MAX_SAMPLES = 12;

const SHAPE_TYPE_KEYS: Record<string, string> = {
  star: "shape.star",
  circle: "shape.circle",
  square: "shape.square",
  triangle: "shape.triangle",
  heart: "shape.heart",
  diamond: "shape.diamond",
  pentagon: "shape.pentagon",
  hexagon: "shape.hexagon",
  octagon: "shape.octagon",
  oval: "shape.oval",
  trapezoid: "shape.trapezoid",
  parallelogram: "shape.parallelogram",
  cloud: "shape.cloud",
  flower: "shape.flower",
  sun: "shape.sun",
};

const buildIntersectionKey = (ids: string[]): string => {
  return ids.slice().sort().join("|");
};

let paintHitCanvas: HTMLCanvasElement | null = null;
let paintHitContext: CanvasRenderingContext2D | null = null;
const paintShapePathCache = new Map<PaintShapeType, Path2D>();

const getPaintHitContext = (): CanvasRenderingContext2D | null => {
  if (paintHitContext) return paintHitContext;
  if (typeof document === "undefined") return null;
  paintHitCanvas = document.createElement("canvas");
  paintHitContext = paintHitCanvas.getContext("2d");
  return paintHitContext;
};

const translateMatrix = (tx: number, ty: number): Matrix => ({
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  e: tx,
  f: ty,
});

const scaleMatrix = (sx: number, sy: number): Matrix => ({
  a: sx,
  b: 0,
  c: 0,
  d: sy,
  e: 0,
  f: 0,
});

const rotateMatrix = (angleDeg: number): Matrix => {
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    a: cos,
    b: sin,
    c: -sin,
    d: cos,
    e: 0,
    f: 0,
  };
};

const multiplyMatrix = (m1: Matrix, m2: Matrix): Matrix => ({
  a: m1.a * m2.a + m1.c * m2.b,
  b: m1.b * m2.a + m1.d * m2.b,
  c: m1.a * m2.c + m1.c * m2.d,
  d: m1.b * m2.c + m1.d * m2.d,
  e: m1.a * m2.e + m1.c * m2.f + m1.e,
  f: m1.b * m2.e + m1.d * m2.f + m1.f,
});

const getElementTransformMatrix = (
  element: EditorElement,
  inset = 0
): Matrix => {
  const rawWidth = element.size.width ?? 0;
  const rawHeight = element.size.height ?? rawWidth ?? 0;
  const width = Math.max(1, rawWidth - inset * 2);
  const height = Math.max(1, rawHeight - inset * 2);
  const angle = element.rotation ?? 0;
  const centerX = width / 2;
  const centerY = height / 2;

  const transforms = [
    translateMatrix(element.position.x + inset, element.position.y + inset),
    translateMatrix(centerX, centerY),
    rotateMatrix(angle),
    translateMatrix(-centerX, -centerY),
    scaleMatrix(width / 100, height / 100),
  ];

  return transforms.reduce((acc, next) => multiplyMatrix(acc, next), {
    a: 1,
    b: 0,
    c: 0,
    d: 1,
    e: 0,
    f: 0,
  });
};

const applyMatrixToPoint = (matrix: Matrix, x: number, y: number) => ({
  x: matrix.a * x + matrix.c * y + matrix.e,
  y: matrix.b * x + matrix.d * y + matrix.f,
});

const matrixToDomMatrix = (matrix: Matrix): DOMMatrix | null => {
  if (typeof DOMMatrix === "undefined") return null;
  return new DOMMatrix([
    matrix.a,
    matrix.b,
    matrix.c,
    matrix.d,
    matrix.e,
    matrix.f,
  ]);
};

const buildPaintShapePath = (
  shapeType: PaintShapeType,
  points?: PaintPoint[] | null
): Path2D | null => {
  if (typeof Path2D === "undefined") return null;
  if (shapeType !== "polygon") {
    const cached = paintShapePathCache.get(shapeType);
    if (cached) return cached;
  }
  const path = new Path2D(getPaintShapePathD(shapeType, points));
  if (shapeType !== "polygon") {
    paintShapePathCache.set(shapeType, path);
  }
  return path;
};

const buildPaintAreaPath = (
  element: EditorElement,
  shapeType: PaintShapeType,
  inset = 0
): Path2D | null => {
  const basePath = buildPaintShapePath(shapeType, getPaintElementPoints(element));
  if (!basePath || typeof Path2D === "undefined") return null;
  const matrix = getElementTransformMatrix(element, inset);
  const domMatrix = matrixToDomMatrix(matrix);
  if (!domMatrix) return null;
  const path = new Path2D();
  path.addPath(basePath, domMatrix);
  return path;
};

const getPaintAreaBounds = (
  element: EditorElement,
  inset = 0
): Bounds => {
  const matrix = getElementTransformMatrix(element, inset);
  const corners = [
    applyMatrixToPoint(matrix, 0, 0),
    applyMatrixToPoint(matrix, 100, 0),
    applyMatrixToPoint(matrix, 100, 100),
    applyMatrixToPoint(matrix, 0, 100),
  ];
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
};

const getOverlapBounds = (a: Bounds, b: Bounds): Bounds | null => {
  const minX = Math.max(a.minX, b.minX);
  const minY = Math.max(a.minY, b.minY);
  const maxX = Math.min(a.maxX, b.maxX);
  const maxY = Math.min(a.maxY, b.maxY);
  if (minX >= maxX || minY >= maxY) return null;
  return { minX, minY, maxX, maxY };
};

const getSampleCount = (size: number): number => {
  const count = Math.round(size / PAINT_INTERSECTION_SAMPLE_STEP);
  return Math.max(
    PAINT_INTERSECTION_MIN_SAMPLES,
    Math.min(PAINT_INTERSECTION_MAX_SAMPLES, count)
  );
};

const collectIntersectionGroups = (
  shapes: PaintAreaShape[]
): Array<{ key: string; ids: string[] }> => {
  const context = getPaintHitContext();
  if (!context) return [];

  const groups = new Map<string, { key: string; ids: string[] }>();

  for (let i = 0; i < shapes.length; i += 1) {
    for (let j = i + 1; j < shapes.length; j += 1) {
      const overlap = getOverlapBounds(shapes[i].bounds, shapes[j].bounds);
      if (!overlap) continue;

      const width = overlap.maxX - overlap.minX;
      const height = overlap.maxY - overlap.minY;
      const xSamples = getSampleCount(width);
      const ySamples = getSampleCount(height);

      for (let xi = 0; xi < xSamples; xi += 1) {
        const x = overlap.minX + ((xi + 0.5) / xSamples) * width;
        for (let yi = 0; yi < ySamples; yi += 1) {
          const y = overlap.minY + ((yi + 0.5) / ySamples) * height;
          const hits = shapes.filter((shape) => {
            if (
              x < shape.bounds.minX ||
              x > shape.bounds.maxX ||
              y < shape.bounds.minY ||
              y > shape.bounds.maxY
            ) {
              return false;
            }
            return context.isPointInPath(shape.path, x, y);
          });

          if (hits.length >= 2) {
            const ids = hits.map((shape) => shape.id);
            const key = buildIntersectionKey(ids);
            if (!groups.has(key)) {
              groups.set(key, { key, ids });
            }
          }
        }
      }
    }
  }

  return Array.from(groups.values()).sort(
    (a, b) => a.ids.length - b.ids.length || a.key.localeCompare(b.key)
  );
};

interface AnswerKeyModalProps {
  canvas: CanvasSettings;
  elements: EditorElement[];
  onClose: () => void;
  onSave: (settings: Partial<CanvasSettings>) => void;
  onUpdateElement?: (
    elementId: string,
    changes: Partial<EditorElement>
  ) => void;
}

const AnswerKeyModal: React.FC<AnswerKeyModalProps> = ({
  canvas,
  elements,
  onClose,
  onSave,
  onUpdateElement,
}) => {
  const { t } = useLocale();
  const shapeTypeLabels = React.useMemo(() => {
    const base: Record<string, string> = Object.fromEntries(
      Object.entries(SHAPE_TYPE_KEYS).map(([k, v]) => [k, t(v)])
    );
    // Cover the shared shape library entries not present in SHAPE_TYPE_KEYS.
    for (const opt of PAINT_SHAPE_PICKER_OPTIONS) {
      if (!base[opt.value]) base[opt.value] = opt.labelTr;
    }
    return base;
  }, [t]);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasInitializedMatchingKey = useRef(false);

  // Answer key state: elementId -> string[] (choice) veya DragDropAnswerConfig (drop-target)
  const [answerKey, setAnswerKey] = useState<Record<string, AnswerKeyValue>>(
    () => canvas.question?.answerKey || {}
  );

  // Modal açıldığında veya canvas.question?.answerKey değiştiğinde state'i güncelle
  useEffect(() => {
    // Modal her açıldığında state'i güncelle (her mount'ta çalışır)
    const currentAnswerKey = canvas.question?.answerKey;
    if (currentAnswerKey) {
      // Deep copy yaparak yeni bir obje oluştur
      const answerKeyCopy = JSON.parse(JSON.stringify(currentAnswerKey));
      setAnswerKey(answerKeyCopy);
    } else {
      setAnswerKey({});
    }
    // Reset initialization flag when modal opens
    hasInitializedMatchingKey.current = false;
  }, [canvas.question?.answerKey]);

  // Get all form elements from the canvas (elements that can have answer keys)
  const formElements = elements.filter((el) => el.type === "choice");
  const dropTargets = elements.filter((el) => el.type === "drop-target");
  const dragSources = elements.filter((el) => el.type === "drag-source");
  const orderLists = elements.filter((el) => el.type === "order-list");
  const matchingPoints = elements.filter((el) => el.type === "matching-point");
  const sourcePoints = matchingPoints.filter((el) => {
    const data = el.data as { pointType?: "source" | "target" };
    return data.pointType === "source";
  });
  const targetPoints = matchingPoints.filter((el) => {
    const data = el.data as { pointType?: "source" | "target" };
    return data.pointType === "target";
  });
  const fillInTheBlanksElements = elements.filter(
    (el) => el.type === "fill-in-the-blanks"
  );
  const paintAreas = elements.filter((el) => el.type === "paint-area");
  const paintSwatches = elements.filter((el) => el.type === "paint-swatch");
  const pushToTalkElements = elements.filter((el) => el.type === "push-to-talk");
  const cubeBars = elements.filter((el) => el.type === "cube-bar");
  const paintAreaLabelLookup = React.useMemo(() => {
    return paintAreas.reduce<
      Record<
        string,
        { label: string; secondary?: string; shapeType: PaintShapeType }
      >
    >((acc, paintArea, index) => {
      const appearance = resolvePaintAreaAppearance(paintArea);
      const shapeType = appearance.shapeType;
      const baseLabel = shapeTypeLabels[shapeType] || shapeType;
      const name = appearance.name?.trim();
      acc[paintArea.id] = {
        label: name ? name : `${baseLabel} ${index + 1}`,
        secondary: name ? baseLabel : undefined,
        shapeType,
      };
      return acc;
    }, {});
  }, [paintAreas]);
  const paintAreaIntersections = React.useMemo(() => {
    if (paintAreas.length < 2) return [];
    const shapes = paintAreas
      .map((paintArea) => {
        const appearance = resolvePaintAreaAppearance(paintArea);
        const path = buildPaintAreaPath(
          paintArea,
          appearance.shapeType,
          PAINT_SHAPE_INSET
        );
        if (!path) return null;
        return {
          id: paintArea.id,
          path,
          bounds: getPaintAreaBounds(paintArea, PAINT_SHAPE_INSET),
        };
      })
      .filter(Boolean) as PaintAreaShape[];
    if (shapes.length < 2) return [];
    return collectIntersectionGroups(shapes);
  }, [paintAreas]);
  const renderPaintColorOptions = (
    config: PaintingAnswerConfig,
    name: string,
    onSelect: (value: PaintingAnswerConfig) => void
  ) => {
    type ColorOption =
      | {
          kind: "swatch";
          id: string;
          color: string;
          label: string;
        }
      | {
          kind: "palette";
          color: string;
          label: string;
        };
    const options: ColorOption[] = [];

    paintSwatches.forEach((swatch) => {
      const swatchAppearance = resolvePaintSwatchAppearance(swatch);
      options.push({
        kind: "swatch",
        id: swatch.id,
        color: swatchAppearance.color,
        label: swatchAppearance.label || swatchAppearance.color,
      });
    });

    elements
      .filter((element) => element.type === "color-palette")
      .forEach((palette) => {
        const paletteData = palette.data as { colors?: string[] };
        const colors = Array.isArray(paletteData.colors)
          ? paletteData.colors
          : [];
        colors.forEach((color) => {
          options.push({
            kind: "palette",
            color,
            label: color,
          });
        });
      });

    if (options.length === 0) {
      return (
        <p className="qe-form__help">
          Canvas üzerinde renk kartelası veya renk alanı bulunamadı. Lütfen önce
          bir renk kartelası ekleyin.
        </p>
      );
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))",
          gap: "6px",
        }}
      >
        {options.map((option, index) => {
          const isSelected =
            option.kind === "swatch"
              ? config.paintSwatchId === option.id
              : config.paintColor?.toLowerCase() === option.color.toLowerCase();

          return (
            <label
              key={`${option.kind}-${index}`}
              title={option.label}
              style={{
                position: "relative",
                cursor: "pointer",
                aspectRatio: "1 / 1",
                backgroundColor: option.color,
                border: `2px solid ${isSelected ? "#3b82f6" : "#e5e7eb"}`,
                borderRadius: "6px",
                boxShadow: isSelected
                  ? "0 0 0 3px rgba(59,130,246,0.25)"
                  : undefined,
                transition: "all 0.2s ease",
              }}
            >
              <input
                type="radio"
                name={name}
                checked={isSelected}
                onChange={() =>
                  onSelect(
                    option.kind === "swatch"
                      ? { paintSwatchId: option.id }
                      : { paintColor: option.color }
                  )
                }
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              {isSelected && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: "#3b82f6",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    border: "2px solid #ffffff",
                  }}
                >
                  ✓
                </span>
              )}
            </label>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [onClose]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // Boşluk doldurma elemanları için cevap anahtarını otomatik olarak ekle
      // Deep copy yaparak yeni bir obje oluştur
      const finalAnswerKey = JSON.parse(JSON.stringify(answerKey));
      fillInTheBlanksElements.forEach((element) => {
        const data = element.data as {
          content?: string;
          blanks?: Array<{ id: string; value: string }>;
        };
        const content = data.content || "";
        const blanks = data.blanks || [];

        // blanks array'inden ID ve değerleri al, yoksa content'ten parse et
        let blankAnswers: Array<{ id: string; value: string }> = [];
        if (blanks.length > 0) {
          // blanks array'inden direkt ID ve value'ları al
          blankAnswers = blanks.map((blank) => ({
            id: blank.id,
            value: blank.value,
          }));
        } else {
          // Fallback: content'ten regex ile parse et (ID oluştur)
          const matches = content.match(/\[(.*?)\]/g);
          if (matches) {
            blankAnswers = matches.map((match, index) => ({
              id: `blank-${Date.now()}-${index}`,
              value: match.slice(1, -1), // Remove brackets
            }));
          }
        }

        // Cevap anahtarına ekle (boş değilse)
        if (blankAnswers.length > 0) {
          finalAnswerKey[element.id] = {
            blanks: blankAnswers,
          };
        }
      });

      // Matching answer key'deki bağlantıları element'lerin data.connections'ına da kaydet
      const matchingKey = Object.keys(finalAnswerKey).find((key) => {
        const value = finalAnswerKey[key];
        return (
          !Array.isArray(value) &&
          !("acceptedItems" in value) &&
          "connections" in value
        );
      });

      // Her source point için connections'ı güncelle
      // matchingKey varsa ondan al, yoksa tüm bağlantıları temizle
      if (onUpdateElement) {
        sourcePoints.forEach((sourcePoint) => {
          let sourceConnections: string[] = [];

          if (matchingKey) {
            const matchingConfig = finalAnswerKey[matchingKey];
            if (
              matchingConfig &&
              !Array.isArray(matchingConfig) &&
              !("acceptedItems" in matchingConfig) &&
              "connections" in matchingConfig
            ) {
              sourceConnections = (
                matchingConfig as MatchingAnswerConfig
              ).connections
                .filter((conn) => conn.sourcePointId === sourcePoint.id)
                .map((conn) => conn.targetPointId);
            }
          }
          // If matchingKey doesn't exist, sourceConnections stays empty array

          const sourceData = sourcePoint.data as { connections?: string[] };
          const currentConnections = sourceData.connections || [];

          // Sadece değişiklik varsa güncelle
          const connectionsChanged =
            sourceConnections.length !== currentConnections.length ||
            sourceConnections.some((id) => !currentConnections.includes(id)) ||
            currentConnections.some((id) => !sourceConnections.includes(id));

          if (connectionsChanged) {
            onUpdateElement(sourcePoint.id, {
              data: {
                ...sourceData,
                connections: sourceConnections,
              },
            });
          }
        });
      }

      // Boş paint config'lerini temizle (eski paintSwatchId veya yeni paintColor)
      Object.keys(finalAnswerKey).forEach((key) => {
        const value = finalAnswerKey[key];
        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          ("paintSwatchId" in value || "paintColor" in value)
        ) {
          const paintingConfig = value as {
            paintSwatchId?: string;
            paintColor?: string;
          };
          const hasSwatch =
            !!paintingConfig.paintSwatchId &&
            paintingConfig.paintSwatchId.trim() !== "";
          const hasColor =
            !!paintingConfig.paintColor &&
            paintingConfig.paintColor.trim() !== "";
          if (!hasSwatch && !hasColor) {
            delete finalAnswerKey[key];
          }
        }
      });

      // push-to-talk için boş/whitespace cevapları temizle (string[] beklenir)
      pushToTalkElements.forEach((element) => {
        const value = finalAnswerKey[element.id];
        if (Array.isArray(value)) {
          const cleaned = value.map((x) => String(x).trim()).filter(Boolean);
          if (cleaned.length > 0) {
            finalAnswerKey[element.id] = cleaned;
          } else {
            delete finalAnswerKey[element.id];
          }
        }
      });

      // Deep copy yaparak yeni bir obje oluştur (referans değişikliği için)
      const answerKeyToSave =
        Object.keys(finalAnswerKey).length > 0
          ? JSON.parse(JSON.stringify(finalAnswerKey))
          : undefined;

      // If answerKey is empty, set it to undefined to properly clear it
      const updates: Partial<CanvasSettings> = {
        question: {
          ...canvas.question,
          answerKey: answerKeyToSave,
        },
      };

      onSave(updates);
      onClose();
    },
    [
      answerKey,
      canvas.question,
      onSave,
      onClose,
      sourcePoints,
      onUpdateElement,
      fillInTheBlanksElements,
      pushToTalkElements,
    ]
  );

  const toggleCorrectAnswer = useCallback(
    (elementId: string, optionValue: string, isMultiple: boolean) => {
      setAnswerKey((prev) => {
        const current = prev[elementId];
        // Choice elementler için değer string[] olmalı
        const currentArray = Array.isArray(current) ? current : [];
        const isSelected = currentArray.includes(optionValue);

        if (isSelected) {
          // Remove from answer key
          const updated = currentArray.filter((v) => v !== optionValue);
          if (updated.length === 0) {
            const { [elementId]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [elementId]: updated };
        } else {
          // Add to answer key
          // For single choice, replace the existing answer
          if (!isMultiple) {
            return { ...prev, [elementId]: [optionValue] };
          }
          // For multiple choice, add to the array
          return { ...prev, [elementId]: [...currentArray, optionValue] };
        }
      });
    },
    []
  );

  const toggleDragSourceForTarget = useCallback(
    (targetId: string, sourceId: string) => {
      setAnswerKey((prev) => {
        const existing = prev[targetId];
        // Drop-target için değer DragDropAnswerConfig olmalı
        const current: DragDropAnswerConfig =
          existing && !Array.isArray(existing) && "acceptedItems" in existing
            ? existing
            : { acceptedItems: [] };

        const isSelected = current.acceptedItems.includes(sourceId);

        if (isSelected) {
          const updated = current.acceptedItems.filter((id) => id !== sourceId);
          if (updated.length === 0) {
            const { [targetId]: _, ...rest } = prev;
            return rest;
          }
          return {
            ...prev,
            [targetId]: { ...current, acceptedItems: updated },
          };
        } else {
          return {
            ...prev,
            [targetId]: {
              ...current,
              acceptedItems: [...current.acceptedItems, sourceId],
            },
          };
        }
      });
    },
    []
  );

  // Kabul edilen öğenin adedini ayarla (tam kaç adet bırakılmalı).
  const setAcceptedCount = useCallback(
    (targetId: string, sourceId: string, count: number) => {
      setAnswerKey((prev) => {
        const existing = prev[targetId];
        const current: DragDropAnswerConfig =
          existing && !Array.isArray(existing) && "acceptedItems" in existing
            ? existing
            : { acceptedItems: [] };
        const n = Math.max(1, Math.min(99, Math.floor(count) || 1));
        return {
          ...prev,
          [targetId]: {
            ...current,
            acceptedCounts: { ...(current.acceptedCounts || {}), [sourceId]: n },
          },
        };
      });
    },
    []
  );

  const reorderAcceptedItems = useCallback(
    (targetId: string, fromIndex: number, toIndex: number) => {
      setAnswerKey((prev) => {
        const existing = prev[targetId];
        const current: DragDropAnswerConfig =
          existing && !Array.isArray(existing) && "acceptedItems" in existing
            ? existing
            : { acceptedItems: [] };

        const items = [...current.acceptedItems];
        const [removed] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, removed);

        return {
          ...prev,
          [targetId]: {
            ...current,
            acceptedItems: items,
          },
        };
      });
    },
    []
  );

  // Reorder items in order-list answer key
  const reorderOrderListItems = useCallback(
    (
      orderListId: string,
      fromIndex: number,
      toIndex: number,
      orderedItemIds: string[]
    ) => {
      setAnswerKey((prev) => {
        // orderedItemIds dizisindeki item ID'lerini kullanarak sıralamayı güncelle
        const items = [...orderedItemIds];
        const [removed] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, removed);

        return {
          ...prev,
          [orderListId]: items,
        };
      });
    },
    []
  );

  // Initialize order-list answer key with default order if not set
  useEffect(() => {
    orderLists.forEach((orderList) => {
      const elementData = orderList.data as { items?: OrderListItem[] };
      const items = elementData.items || [];
      if (items.length > 0) {
        setAnswerKey((prev) => {
          const existing = prev[orderList.id];
          // Only set if not already set or empty
          if (!existing || !Array.isArray(existing) || existing.length === 0) {
            const defaultOrder = items.map((item) => item.id);
            return { ...prev, [orderList.id]: defaultOrder };
          }
          return prev;
        });
      }
    });
  }, [orderLists]); // Remove answerKey from deps to avoid infinite loop

  // Initialize matching answer key from element connections
  // Only runs once on mount, not when answerKey changes (to allow users to clear all connections)
  useEffect(() => {
    if (sourcePoints.length === 0) return;
    if (hasInitializedMatchingKey.current) return; // Already initialized once

    setAnswerKey((prev) => {
      // Check if matching answer key already exists
      const existingMatchingKey = Object.entries(prev).find(
        ([_, value]) =>
          !Array.isArray(value) &&
          !("acceptedItems" in value) &&
          "connections" in value
      );

      if (existingMatchingKey) {
        hasInitializedMatchingKey.current = true;
        return prev; // Already initialized
      }

      // Build connections from element data
      const connections: Array<{
        sourcePointId: string;
        targetPointId: string;
      }> = [];
      sourcePoints.forEach((sourcePoint) => {
        const sourceData = sourcePoint.data as { connections?: string[] };
        const connectionsList = sourceData.connections || [];
        connectionsList.forEach((targetId) => {
          connections.push({
            sourcePointId: sourcePoint.id,
            targetPointId: targetId,
          });
        });
      });

      if (connections.length > 0) {
        // Use a special key for matching answer key
        const matchingKey = "matching-connections";
        hasInitializedMatchingKey.current = true;
        return {
          ...prev,
          [matchingKey]: { connections } as MatchingAnswerConfig,
        };
      }

      hasInitializedMatchingKey.current = true;
      return prev;
    });
  }, [sourcePoints]); // Only depend on sourcePoints

  // Toggle matching connection
  const toggleMatchingConnection = useCallback(
    (sourceId: string, targetId: string) => {
      setAnswerKey((prev) => {
        // Find existing matching answer key
        const matchingKey =
          Object.keys(prev).find((key) => {
            const value = prev[key];
            return (
              !Array.isArray(value) &&
              !("acceptedItems" in value) &&
              "connections" in value
            );
          }) || "matching-connections";

        const existing = prev[matchingKey];
        const current: MatchingAnswerConfig =
          existing &&
          !Array.isArray(existing) &&
          !("acceptedItems" in existing) &&
          "connections" in existing
            ? existing
            : { connections: [] };

        const isSelected = current.connections.some(
          (conn) =>
            conn.sourcePointId === sourceId && conn.targetPointId === targetId
        );

        if (isSelected) {
          const updated = current.connections.filter(
            (conn) =>
              !(
                conn.sourcePointId === sourceId &&
                conn.targetPointId === targetId
              )
          );
          if (updated.length === 0) {
            const { [matchingKey]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [matchingKey]: { connections: updated } };
        } else {
          return {
            ...prev,
            [matchingKey]: {
              connections: [
                ...current.connections,
                { sourcePointId: sourceId, targetPointId: targetId },
              ],
            },
          };
        }
      });
    },
    []
  );

  return (
    <div className="qe-modal" role="presentation">
      <div
        className="qe-modal__backdrop"
        onClick={onClose}
        role="button"
        aria-label={t("answerKey.close")}
      />
      <div
        className="qe-modal__content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qe-answer-key-title"
        tabIndex={-1}
        ref={contentRef}
        style={{ maxWidth: "600px" }}
      >
        <header className="qe-modal__header">
          <div>
            <h2 id="qe-answer-key-title">{t("answerKey.title")}</h2>
            <p className="qe-modal__subtitle">
              {t("answerKey.subtitle")}
            </p>
          </div>
          <button
            className="qe-button qe-button--ghost"
            type="button"
            onClick={onClose}
          >
            {t("answerKey.close")}
          </button>
        </header>
        <form className="qe-modal__body" onSubmit={handleSubmit}>
          {formElements.length === 0 &&
          dropTargets.length === 0 &&
          orderLists.length === 0 &&
          sourcePoints.length === 0 &&
          fillInTheBlanksElements.length === 0 &&
          paintAreas.length === 0 &&
          pushToTalkElements.length === 0 &&
          cubeBars.length === 0 ? (
            <section className="qe-modal__section">
              <p className="qe-form__help">
                {t("answerKey.noFormElements")}
              </p>
            </section>
          ) : (
            <>
              {/* Lego/Unifix küp çubuğu — beklenen toplam (cevap) */}
              {cubeBars.length > 0 && (
                <section className="qe-modal__section">
                  <h3
                    style={{
                      fontSize: "16px",
                      marginBottom: "12px",
                      color: "#1e293b",
                      fontWeight: 600,
                    }}
                  >
                    Küp Çubukları (Beklenen Toplam)
                  </h3>
                  <p className="qe-form__help" style={{ marginBottom: 12 }}>
                    İşlem sonunda hedef alanda olması beklenen toplam küp
                    sayısı. Örn: başlangıçta 3 küp + öğrenci 2 ekler = 5.
                    Beklenen toplam başlangıçtan küçükse çıkarma sorusu olur.
                  </p>
                  {cubeBars.map((bar) => {
                    const existing = answerKey[bar.id];
                    const config: { expectedTotal?: number } =
                      existing &&
                      !Array.isArray(existing) &&
                      "expectedTotal" in existing
                        ? (existing as { expectedTotal: number })
                        : {};
                    const initialValue =
                      (bar.data as { value?: number }).value ?? 0;
                    const slotCount =
                      (bar.data as { slotCount?: number }).slotCount ?? 10;
                    return (
                      <div
                        key={bar.id}
                        style={{
                          padding: "12px",
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: 6,
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            color: "#475569",
                            marginBottom: 6,
                          }}
                        >
                          <strong>Küp Çubuğu</strong> · {slotCount} yuvalı,
                          başlangıç {initialValue} dolu
                        </div>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 13,
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            Beklenen Toplam:
                          </span>
                          <input
                            type="number"
                            min={0}
                            max={slotCount}
                            step={1}
                            value={config.expectedTotal ?? ""}
                            placeholder="0"
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "") {
                                setAnswerKey((prev) => {
                                  const next = { ...prev };
                                  delete next[bar.id];
                                  return next;
                                });
                                return;
                              }
                              const n = Math.max(
                                0,
                                Math.min(slotCount, Number(v) || 0)
                              );
                              setAnswerKey((prev) => ({
                                ...prev,
                                [bar.id]: { expectedTotal: n },
                              }));
                            }}
                            style={{
                              width: 80,
                              padding: "6px 8px",
                              border: "1px solid #cbd5e1",
                              borderRadius: 4,
                              fontSize: 14,
                            }}
                          />
                          <span style={{ color: "#94a3b8", fontSize: 12 }}>
                            (0–{slotCount})
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </section>
              )}
              {formElements.length > 0 && (
                <section className="qe-modal__section">
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.choiceQuestions")}
                  </h3>
                  {formElements.map((element) => {
                    const elementData = element.data as {
                      options?: ChoiceOption[];
                      multiple?: boolean;
                    };
                    const options = elementData.options || [];
                    const isMultiple = elementData.multiple || false;
                    const existing = answerKey[element.id];
                    const correctAnswers: string[] = Array.isArray(existing)
                      ? existing
                      : [];

                    return (
                      <div key={element.id} className="qe-answer-key-section">
                        <div className="qe-answer-key-header">
                          <strong>Soru: {element.id}</strong>
                          <span className="qe-answer-key-type">
                            ({isMultiple ? t("answerKey.choice.multipleSelection") : t("answerKey.choice.singleSelection")})
                          </span>
                        </div>

                        {!isMultiple && correctAnswers.length > 0 && (
                          <p
                            className="qe-form__help"
                            style={{ marginBottom: "8px", fontSize: "12px" }}
                          >
                            {t("answerKey.choice.singleSelectionHint")}
                          </p>
                        )}

                        <div className="qe-answer-key-options">
                          {options.map((option, idx) => {
                            const isCorrect = correctAnswers.includes(
                              option.value
                            );
                            const hasMathContent =
                              (typeof option.mathml === "string" &&
                                option.mathml.trim().length > 0) ||
                              (typeof option.latex === "string" &&
                                option.latex.trim().length > 0);
                            const contentType =
                              option.contentType === "text" ||
                              !option.contentType
                                ? hasMathContent
                                  ? "mathtype"
                                  : "text"
                                : option.contentType;
                            const displayLabel =
                              option.label?.trim() ||
                              option.imageAlt?.trim() ||
                              (contentType === "image"
                                ? `${t("renderers.choice.imagePlaceholder")} ${idx + 1}`
                                : option.value);
                            return (
                              <label
                                key={option.id}
                                className={`qe-answer-key-option ${
                                  isCorrect
                                    ? "qe-answer-key-option--selected"
                                    : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isCorrect}
                                  onChange={() =>
                                    toggleCorrectAnswer(
                                      element.id,
                                      option.value,
                                      isMultiple
                                    )
                                  }
                                />
                                <span className="qe-answer-key-option-label">
                                  {displayLabel}
                                </span>
                                <span className="qe-answer-key-option-value">
                                  (ID: {option.value})
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {fillInTheBlanksElements.length > 0 && (
                <section
                  className="qe-modal__section"
                  style={{ marginTop: formElements.length > 0 ? "24px" : "0" }}
                >
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.fillInTheBlanks")}
                  </h3>
                  {fillInTheBlanksElements.map((element) => {
                    const data = element.data as {
                      content?: string;
                      blanks?: Array<{ id: string; value: string }>;
                    };
                    const content = data.content || "";
                    const blanks = data.blanks || [];

                    // Fallback to regex parsing if blanks array is not available
                    const matches = content.match(/\[(.*?)\]/g);
                    const answers =
                      blanks.length > 0
                        ? blanks
                        : matches
                        ? matches.map((m) => ({
                            id: `blank-${Math.random()}`,
                            value: m.slice(1, -1),
                          }))
                        : [];

                    return (
                      <div key={element.id} className="qe-answer-key-section">
                        <div className="qe-answer-key-header">
                          <strong>Soru: {element.id}</strong>
                          <span className="qe-answer-key-type">
                            ({t("answerKey.fillInTheBlanks.label")})
                          </span>
                        </div>

                        <div className="qe-answer-key-options">
                          {answers.length === 0 ? (
                            <p className="qe-form__help">
                              {t("answerKey.fillInTheBlanks.noAnswers")}
                            </p>
                          ) : (
                            answers.map((blank, idx) => (
                              <div
                                key={blank.id}
                                className="qe-answer-key-option qe-answer-key-option--selected"
                                style={{ cursor: "default" }}
                              >
                                <span className="qe-answer-key-option-label">
                                  {idx + 1}.{" "}
                                  <strong>{blank.value}</strong>
                                </span>
                                <span
                                  className="qe-answer-key-type"
                                  style={{ fontSize: "11px", opacity: 0.7 }}
                                >
                                  (ID: {blank.id})
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {pushToTalkElements.length > 0 && (
                <section
                  className="qe-modal__section"
                  style={{
                    marginTop:
                      formElements.length > 0 ||
                      fillInTheBlanksElements.length > 0
                        ? "24px"
                        : "0",
                  }}
                >
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.pushToTalk")}
                  </h3>
                  <p className="qe-form__help" style={{ marginBottom: 12 }}>
                    {t("answerKey.pushToTalk.instruction")}
                  </p>
                  {pushToTalkElements.map((element) => {
                    const existing = answerKey[element.id];
                    const correctAnswers: string[] = Array.isArray(existing)
                      ? existing
                      : [];
                    return (
                      <div key={element.id} className="qe-answer-key-section">
                        <div className="qe-answer-key-header">
                          <strong>Eleman: {element.id}</strong>
                          <span className="qe-answer-key-type">
                            (push-to-talk)
                          </span>
                        </div>
                        <div style={{ display: "grid", gap: 8 }}>
                          {correctAnswers.length === 0 && (
                            <p className="qe-form__help" style={{ margin: 0 }}>
                              {t("answerKey.pushToTalk.noAnswers")}
                            </p>
                          )}
                          {correctAnswers.map((val, idx) => (
                            <div
                              key={`${element.id}-ptt-ak-${idx}`}
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              }}
                            >
                              <input
                                className="qe-input"
                                style={{ flex: 1 }}
                                placeholder={`${idx + 1}`}
                                value={val}
                                onChange={(e) => {
                                  const next = [...correctAnswers];
                                  next[idx] = e.target.value;
                                  setAnswerKey((prev) => ({
                                    ...prev,
                                    [element.id]: next,
                                  }));
                                }}
                              />
                              <button
                                type="button"
                                className="qe-button qe-button--ghost"
                                onClick={() => {
                                  const next = correctAnswers.filter(
                                    (_, i) => i !== idx
                                  );
                                  setAnswerKey((prev) => {
                                    if (next.length === 0) {
                                      const { [element.id]: _, ...rest } = prev;
                                      return rest;
                                    }
                                    return { ...prev, [element.id]: next };
                                  });
                                }}
                              >
                                {t("contextMenu.delete")}
                              </button>
                            </div>
                          ))}
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              className="qe-button qe-button--secondary"
                              onClick={() => {
                                setAnswerKey((prev) => {
                                  const current = prev[element.id];
                                  const arr = Array.isArray(current)
                                    ? [...current]
                                    : [];
                                  arr.push("");
                                  return { ...prev, [element.id]: arr };
                                });
                              }}
                            >
                              {t("props.addOption")}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {paintAreas.length > 0 && (
                <section
                  className="qe-modal__section"
                  style={{
                    marginTop:
                      formElements.length > 0 ||
                      fillInTheBlanksElements.length > 0
                        ? "24px"
                        : "0",
                  }}
                >
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.paintAreas")}
                  </h3>
                  {paintAreas.map((paintArea) => {
                    const existing = answerKey[paintArea.id];
                    const config: PaintingAnswerConfig =
                      existing &&
                      !Array.isArray(existing) &&
                      ("paintSwatchId" in existing || "paintColor" in existing)
                        ? (existing as PaintingAnswerConfig)
                        : {};

                    const paintAreaAppearance =
                      resolvePaintAreaAppearance(paintArea);
                    const shapeType = paintAreaAppearance.shapeType;
                    const labelInfo = paintAreaLabelLookup[paintArea.id];
                    const displayLabel =
                      labelInfo?.label ||
                      shapeTypeLabels[shapeType] ||
                      shapeType;

                    // Render shape preview
                    const renderShapePreview = () => {
                      const size = 40;
                      const shapeStyle: React.CSSProperties = {
                        width: `${size}px`,
                        height: `${size}px`,
                        flexShrink: 0,
                      };

                      const previewPoints = getPaintElementPoints(paintArea);
                      return (
                        <svg
                          style={shapeStyle}
                          viewBox="0 0 100 100"
                          preserveAspectRatio="none"
                        >
                          <path
                            d={getPaintShapePathD(shapeType, previewPoints)}
                            fill="none"
                            stroke="#cbd5e1"
                            strokeWidth="2"
                          />
                        </svg>
                      );
                    };

                    return (
                      <div
                        key={paintArea.id}
                        className="qe-answer-key-section"
                        style={{ marginBottom: "24px" }}
                      >
                        <div
                          className="qe-answer-key-header"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          {renderShapePreview()}
                          <div>
                            <strong>{displayLabel}</strong>
                            {labelInfo?.secondary && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  marginTop: "2px",
                                }}
                              >
                                {labelInfo.secondary}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="qe-answer-key-options">
                          {renderPaintColorOptions(
                            config,
                            `paint-area-${paintArea.id}`,
                            (value) =>
                              setAnswerKey((prev) => ({
                                ...prev,
                                [paintArea.id]: value,
                              }))
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {paintAreaIntersections.length > 0 && (
                    <>
                      <div
                        style={{
                          marginTop: "16px",
                          marginBottom: "12px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        {t("answerKey.section.intersections")}
                      </div>
                      {paintAreaIntersections.map((group) => {
                        const intersectionId = `${PAINT_INTERSECTION_PREFIX}${group.key}`;
                        const existing = answerKey[intersectionId];
                        const config: PaintingAnswerConfig =
                          existing &&
                          !Array.isArray(existing) &&
                          ("paintSwatchId" in existing || "paintColor" in existing)
                            ? (existing as PaintingAnswerConfig)
                            : {};
                        const labelParts = group.ids.map(
                          (id) => paintAreaLabelLookup[id]?.label || id
                        );
                        const displayLabel = labelParts.join(" + ");

                        return (
                          <div
                            key={intersectionId}
                            className="qe-answer-key-section"
                            style={{ marginBottom: "24px" }}
                          >
                            <div
                              className="qe-answer-key-header"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <div>
                                <strong>{t("answerKey.section.intersections")}: {displayLabel}</strong>
                              </div>
                            </div>

                            <div className="qe-answer-key-options">
                              {renderPaintColorOptions(
                                config,
                                `paint-intersection-${group.key}`,
                                (value) =>
                                  setAnswerKey((prev) => ({
                                    ...prev,
                                    [intersectionId]: value,
                                  }))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </section>
              )}

              {dropTargets.length > 0 && (
                <section
                  className="qe-modal__section"
                  style={{
                    marginTop:
                      formElements.length > 0 ||
                      fillInTheBlanksElements.length > 0
                        ? "24px"
                        : "0",
                  }}
                >
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.dragDrop")}
                  </h3>
                  {dropTargets.map((target) => {
                    const targetData = target.data as { label?: string };
                    const targetLabel = targetData.label || target.id;
                    const existing = answerKey[target.id];
                    const config: DragDropAnswerConfig =
                      existing &&
                      !Array.isArray(existing) &&
                      "acceptedItems" in existing
                        ? existing
                        : { acceptedItems: [] };

                    return (
                      <div
                        key={target.id}
                        className="qe-answer-key-section"
                        style={{ marginBottom: "24px" }}
                      >
                        <div className="qe-answer-key-header">
                          <strong>Hedef Alan: {targetLabel}</strong>
                          <span className="qe-answer-key-type">
                            (ID: {target.id})
                          </span>
                        </div>

                        <div
                          style={{ marginTop: "12px", marginBottom: "12px" }}
                        >
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            {t("answerKey.dragDrop.existingItems")}
                          </label>
                          {dragSources.length === 0 ? (
                            <p
                              className="qe-form__help"
                              style={{ fontSize: "12px" }}
                            >
                              {t("answerKey.dragDrop.noItems")}
                            </p>
                          ) : (
                            <div className="qe-answer-key-options">
                              {dragSources.map((source) => {
                                const sourceData = source.data as {
                                  label?: string;
                                };
                                const sourceLabel =
                                  sourceData.label || source.id;
                                const isAccepted =
                                  config.acceptedItems.includes(source.id);

                                return (
                                  <label
                                    key={source.id}
                                    className={`qe-answer-key-option ${
                                      isAccepted
                                        ? "qe-answer-key-option--selected"
                                        : ""
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isAccepted}
                                      onChange={() =>
                                        toggleDragSourceForTarget(
                                          target.id,
                                          source.id
                                        )
                                      }
                                    />
                                    <span className="qe-answer-key-option-label">
                                      {sourceLabel}
                                    </span>
                                    <span className="qe-answer-key-option-value">
                                      (ID: {source.id})
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {config.acceptedItems.length > 0 && (
                          <div
                            style={{
                              marginTop: "16px",
                              marginBottom: "12px",
                              padding: "12px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              border: "1px solid #86efac",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "8px",
                              }}
                            >
                              <label
                                style={{ fontSize: "13px", fontWeight: 500 }}
                              >
                                {t("answerKey.dragDrop.acceptedItems")}{" "}
                                {config.orderMatters ? t("answerKey.dragDrop.ordered") : t("answerKey.dragDrop.unordered")}
                                :
                              </label>
                              {config.orderMatters && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#059669",
                                    fontWeight: 500,
                                  }}
                                >
                                  {t("answerKey.dragDrop.orderWarning")}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {config.acceptedItems.map((sourceId, index) => {
                                const source = dragSources.find(
                                  (s) => s.id === sourceId
                                );
                                const sourceData = source?.data as
                                  | { label?: string }
                                  | undefined;
                                const sourceLabel =
                                  sourceData?.label || sourceId;

                                return (
                                  <div
                                    key={sourceId}
                                    draggable={config.orderMatters}
                                    onDragStart={(e) => {
                                      if (config.orderMatters) {
                                        e.dataTransfer.effectAllowed = "move";
                                        e.dataTransfer.setData(
                                          "text/plain",
                                          index.toString()
                                        );
                                      }
                                    }}
                                    onDragOver={(e) => {
                                      if (config.orderMatters) {
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = "move";
                                      }
                                    }}
                                    onDrop={(e) => {
                                      if (config.orderMatters) {
                                        e.preventDefault();
                                        const fromIndex = parseInt(
                                          e.dataTransfer.getData("text/plain"),
                                          10
                                        );
                                        reorderAcceptedItems(
                                          target.id,
                                          fromIndex,
                                          index
                                        );
                                      }
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      padding: "8px 12px",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1fae5",
                                      borderRadius: "4px",
                                      cursor: config.orderMatters
                                        ? "grab"
                                        : "default",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      color: "#065f46",
                                    }}
                                  >
                                    {config.orderMatters && (
                                      <span
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: "20px",
                                          height: "20px",
                                          backgroundColor: "#10b981",
                                          color: "white",
                                          borderRadius: "50%",
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {index + 1}
                                      </span>
                                    )}
                                    {!config.orderMatters && (
                                      <span style={{ fontSize: "14px" }}>
                                        ✓
                                      </span>
                                    )}
                                    <span style={{ flex: 1 }}>
                                      {sourceLabel}
                                    </span>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                        color: "#065f46",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Adet:
                                      <input
                                        type="number"
                                        min={1}
                                        max={99}
                                        value={
                                          config.acceptedCounts?.[sourceId] ?? 1
                                        }
                                        onChange={(e) =>
                                          setAcceptedCount(
                                            target.id,
                                            sourceId,
                                            Number(e.target.value) || 1
                                          )
                                        }
                                        style={{
                                          width: 52,
                                          padding: "4px 6px",
                                          border: "1px solid #86efac",
                                          borderRadius: 4,
                                          fontSize: 13,
                                        }}
                                        title="Bu öğeden tam kaç adet bırakılmalı"
                                      />
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleDragSourceForTarget(
                                          target.id,
                                          sourceId
                                        )
                                      }
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#dc2626",
                                        cursor: "pointer",
                                        fontSize: "16px",
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                      title={t("answerKey.dragDrop.removeTitle")}
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              )}

              {sourcePoints.length > 0 && (
                <section
                  className="qe-modal__section"
                  style={{
                    marginTop:
                      formElements.length > 0 ||
                      dropTargets.length > 0 ||
                      orderLists.length > 0 ||
                      fillInTheBlanksElements.length > 0
                        ? "24px"
                        : "0",
                  }}
                >
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.matching")}
                  </h3>
                  {(() => {
                    // Get matching answer key
                    const matchingKey =
                      Object.keys(answerKey).find((key) => {
                        const value = answerKey[key];
                        return (
                          !Array.isArray(value) &&
                          !("acceptedItems" in value) &&
                          "connections" in value
                        );
                      }) || "matching-connections";
                    const existing = answerKey[matchingKey];
                    const config: MatchingAnswerConfig =
                      existing &&
                      !Array.isArray(existing) &&
                      !("acceptedItems" in existing) &&
                      "connections" in existing
                        ? existing
                        : { connections: [] };

                    // Helper function to get display label for matching point
                    const getMatchingPointLabel = (
                      point: EditorElement,
                      index: number
                    ): string => {
                      const data = point.data as {
                        name?: string;
                        label?: string;
                      };
                      // Önce name'e bak, sonra label'a, en son index'e göre isim ver
                      if (data.name && data.name.trim()) {
                        return data.name.trim();
                      }
                      if (data.label && data.label.trim()) {
                        return data.label.trim();
                      }
                      return `${index + 1}`;
                    };

                    if (targetPoints.length === 0) {
                      return (
                        <p
                          className="qe-form__help"
                          style={{ fontSize: "12px", marginTop: "12px" }}
                        >
                          {t("answerKey.matching.noTargets")}
                        </p>
                      );
                    }

                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "20px",
                        }}
                      >
                        {sourcePoints.map((sourcePoint, sourceIndex) => {
                          const sourceLabel = getMatchingPointLabel(
                            sourcePoint,
                            sourceIndex
                          );
                          const sourceData = sourcePoint.data as {
                            appearance?: { sourceColor?: string };
                          };
                          const sourceColor =
                            sourceData.appearance?.sourceColor || "#2563eb";

                          // Get connections for this source point
                          const sourceConnections = config.connections.filter(
                            (conn) => conn.sourcePointId === sourcePoint.id
                          );

                          return (
                            <div
                              key={sourcePoint.id}
                              style={{
                                padding: "16px",
                                backgroundColor: "#f9fafb",
                                borderRadius: "8px",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              {/* Source Point Header */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  marginBottom: "16px",
                                  paddingBottom: "12px",
                                  borderBottom: "2px solid #e5e7eb",
                                }}
                              >
                                <div
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: sourceColor,
                                    border: `3px solid ${sourceColor}80`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    boxShadow: `0 0 0 2px ${sourceColor}20`,
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "white",
                                      fontSize: "14px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {sourceIndex + 1}
                                  </span>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#111827",
                                    }}
                                  >
                                    {sourceLabel}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {t("answerKey.matching.sourcePoint")}
                                  </div>
                                </div>
                              </div>

                              {/* Target Points Grid */}
                              <div style={{ marginTop: "12px" }}>
                                <label
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    marginBottom: "12px",
                                    display: "block",
                                    color: "#6b7280",
                                  }}
                                >
                                  {t("answerKey.matching.availableTargets")}
                                </label>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "repeat(auto-fill, minmax(200px, 1fr))",
                                    gap: "10px",
                                  }}
                                >
                                  {targetPoints.map(
                                    (targetPoint, targetIndex) => {
                                      const targetLabel = getMatchingPointLabel(
                                        targetPoint,
                                        targetIndex
                                      );
                                      const targetData = targetPoint.data as {
                                        appearance?: { targetColor?: string };
                                      };
                                      const targetColor =
                                        targetData.appearance?.targetColor ||
                                        "#10b981";
                                      const isConnected =
                                        sourceConnections.some(
                                          (conn) =>
                                            conn.targetPointId ===
                                            targetPoint.id
                                        );

                                      return (
                                        <label
                                          key={targetPoint.id}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            padding: "12px",
                                            backgroundColor: isConnected
                                              ? "#f0fdf4"
                                              : "#ffffff",
                                            border: `2px solid ${
                                              isConnected
                                                ? targetColor
                                                : "#e5e7eb"
                                            }`,
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            position: "relative",
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isConnected) {
                                              e.currentTarget.style.borderColor =
                                                targetColor;
                                              e.currentTarget.style.backgroundColor =
                                                "#f0fdf4";
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isConnected) {
                                              e.currentTarget.style.borderColor =
                                                "#e5e7eb";
                                              e.currentTarget.style.backgroundColor =
                                                "#ffffff";
                                            }
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isConnected}
                                            onChange={() =>
                                              toggleMatchingConnection(
                                                sourcePoint.id,
                                                targetPoint.id
                                              )
                                            }
                                            style={{
                                              width: "18px",
                                              height: "18px",
                                              cursor: "pointer",
                                              accentColor: targetColor,
                                            }}
                                          />
                                          <div
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              borderRadius: "50%",
                                              backgroundColor: targetColor,
                                              border: `2px solid ${targetColor}80`,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                              boxShadow: `0 0 0 2px ${targetColor}20`,
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "white",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                              }}
                                            >
                                              {targetIndex + 1}
                                            </span>
                                          </div>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                              style={{
                                                fontSize: "13px",
                                                fontWeight: isConnected
                                                  ? 600
                                                  : 500,
                                                color: isConnected
                                                  ? "#065f46"
                                                  : "#111827",
                                              }}
                                            >
                                              {targetLabel}
                                            </div>
                                          </div>
                                          {isConnected && (
                                            <div
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                backgroundColor: targetColor,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  color: "white",
                                                  fontSize: "12px",
                                                }}
                                              >
                                                ✓
                                              </span>
                                            </div>
                                          )}
                                        </label>
                                      );
                                    }
                                  )}
                                </div>
                              </div>

                              {/* Connected Targets Summary */}
                              {sourceConnections.length > 0 && (
                                <div
                                  style={{
                                    marginTop: "16px",
                                    padding: "12px",
                                    backgroundColor: "#fef3c7",
                                    borderRadius: "6px",
                                    border: "1px solid #fcd34d",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#92400e",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    {t("answerKey.matching.connectedTargets")}{sourceConnections.length}):
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "6px",
                                    }}
                                  >
                                    {sourceConnections.map((conn) => {
                                      const targetPoint = targetPoints.find(
                                        (t) => t.id === conn.targetPointId
                                      );
                                      const targetIndex = targetPoint
                                        ? targetPoints.indexOf(targetPoint)
                                        : -1;
                                      const targetLabel = targetPoint
                                        ? getMatchingPointLabel(
                                            targetPoint,
                                            targetIndex
                                          )
                                        : conn.targetPointId;
                                      const targetData = targetPoint?.data as
                                        | {
                                            appearance?: {
                                              targetColor?: string;
                                            };
                                          }
                                        | undefined;
                                      const targetColor =
                                        targetData?.appearance?.targetColor ||
                                        "#10b981";

                                      return (
                                        <div
                                          key={`${conn.sourcePointId}-${conn.targetPointId}`}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            padding: "6px 10px",
                                            backgroundColor: "#ffffff",
                                            border: `1px solid ${targetColor}`,
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            color: "#065f46",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "16px",
                                              height: "16px",
                                              borderRadius: "50%",
                                              backgroundColor: targetColor,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "white",
                                                fontSize: "9px",
                                                fontWeight: 600,
                                              }}
                                            >
                                              {targetIndex >= 0
                                                ? targetIndex + 1
                                                : "?"}
                                            </span>
                                          </div>
                                          <span>{targetLabel}</span>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleMatchingConnection(
                                                conn.sourcePointId,
                                                conn.targetPointId
                                              )
                                            }
                                            style={{
                                              background: "transparent",
                                              border: "none",
                                              color: "#dc2626",
                                              cursor: "pointer",
                                              fontSize: "14px",
                                              padding: "0 4px",
                                              display: "flex",
                                              alignItems: "center",
                                              lineHeight: 1,
                                            }}
                                            title={t("answerKey.matching.removeConnectionTitle")}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </section>
              )}

              {orderLists.length > 0 && (
                <section
                  className="qe-modal__section"
                  style={{
                    marginTop:
                      formElements.length > 0 ||
                      dropTargets.length > 0 ||
                      sourcePoints.length > 0 ||
                      fillInTheBlanksElements.length > 0
                        ? "24px"
                        : "0",
                  }}
                >
                  <h3
                    style={{
                      marginBottom: "16px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("answerKey.section.orderList")}
                  </h3>
                  {orderLists.map((orderList) => {
                    const orderListData = orderList.data as {
                      label?: string;
                      items?: OrderListItem[];
                    };
                    const orderListLabel = orderListData.label || orderList.id;
                    const items = orderListData.items || [];
                    const existing = answerKey[orderList.id];
                    const correctOrder: string[] = Array.isArray(existing)
                      ? existing
                      : [];

                    // Get ordered items based on correctOrder, with fallback to original items order
                    const orderedItems: OrderListItem[] =
                      correctOrder.length > 0
                        ? [
                            ...correctOrder
                              .map((itemId) =>
                                items.find((item) => item.id === itemId)
                              )
                              .filter((item): item is OrderListItem =>
                                Boolean(item)
                              ),
                            ...items.filter(
                              (item) => !correctOrder.includes(item.id)
                            ),
                          ]
                        : items;

                    return (
                      <div
                        key={orderList.id}
                        className="qe-answer-key-section"
                        style={{ marginBottom: "24px" }}
                      >
                        <div className="qe-answer-key-header">
                          <strong>{t("answerKey.section.orderList")}: {orderListLabel}</strong>
                          <span className="qe-answer-key-type">
                            (ID: {orderList.id})
                          </span>
                        </div>

                        {items.length === 0 ? (
                          <p
                            className="qe-form__help"
                            style={{ fontSize: "12px", marginTop: "12px" }}
                          >
                            {t("answerKey.orderList.noItems")}
                          </p>
                        ) : (
                          <>
                            <div
                              style={{
                                marginTop: "16px",
                                marginBottom: "12px",
                                padding: "12px",
                                backgroundColor: "#f0f9ff",
                                borderRadius: "6px",
                                border: "1px solid #7dd3fc",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: "8px",
                                }}
                              >
                                <label
                                  style={{ fontSize: "13px", fontWeight: 500 }}
                                >
                                  {t("answerKey.orderList.correctOrder")}
                                </label>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#0369a1",
                                    fontWeight: 500,
                                  }}
                                >
                                  {t("answerKey.orderList.dragHint")}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "6px",
                                }}
                              >
                                {orderedItems.map((item, index) => {
                                  const hasMathContent =
                                    (typeof item.mathml === "string" &&
                                      item.mathml.trim().length > 0) ||
                                    (typeof item.latex === "string" &&
                                      item.latex.trim().length > 0);
                                  const contentType =
                                    item.contentType === "text" ||
                                    !item.contentType
                                      ? hasMathContent
                                        ? "mathtype"
                                        : "text"
                                      : item.contentType;
                                  const isMathContent =
                                    contentType === "mathtype" ||
                                    contentType === "mathlive";
                                  const mathml =
                                    item.mathml || item.latex || "";
                                  const displayLabel =
                                    isMathContent
                                      ? t("props.contentTypeMath")
                                      : contentType === "text"
                                      ? item.label?.trim() || `${index + 1}`
                                      : item.imageAlt?.trim() ||
                                        item.imageUrl ||
                                        `${t("props.image")} ${index + 1}`;

                                  return (
                                    <div
                                      key={item.id}
                                      draggable={true}
                                      onDragStart={(e) => {
                                        e.dataTransfer.effectAllowed = "move";
                                        e.dataTransfer.setData(
                                          "text/plain",
                                          index.toString()
                                        );
                                      }}
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.dataTransfer.dropEffect = "move";
                                      }}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const fromIndex = parseInt(
                                          e.dataTransfer.getData("text/plain"),
                                          10
                                        );
                                        // orderedItems dizisindeki tüm item ID'lerini gönder
                                        const orderedItemIds = orderedItems.map(
                                          (item) => item.id
                                        );
                                        reorderOrderListItems(
                                          orderList.id,
                                          fromIndex,
                                          index,
                                          orderedItemIds
                                        );
                                      }}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "8px 12px",
                                        backgroundColor: "#ffffff",
                                        border: "1px solid #bae6fd",
                                        borderRadius: "4px",
                                        cursor: "grab",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        color: "#0c4a6e",
                                        transition: "all 0.2s ease",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor =
                                          "#7dd3fc";
                                        e.currentTarget.style.backgroundColor =
                                          "#f0f9ff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor =
                                          "#bae6fd";
                                        e.currentTarget.style.backgroundColor =
                                          "#ffffff";
                                      }}
                                      >
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "24px",
                                            height: "24px",
                                            backgroundColor: "#0284c7",
                                            color: "white",
                                            borderRadius: "50%",
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            flexShrink: 0,
                                          }}
                                        >
                                          {index + 1}
                                        </span>
                                      {isMathContent ? (
                                        <div
                                          style={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                          }}
                                        >
                                          {mathml ? (
                                            <MathMLContent
                                              html={mathml}
                                              style={{
                                                fontSize: "18px",
                                                color: "#0c4a6e",
                                              }}
                                            />
                                          ) : (
                                            <span>{displayLabel}</span>
                                          )}
                                        </div>
                                      ) : (
                                        <span style={{ flex: 1 }}>
                                          {displayLabel}
                                        </span>
                                      )}
                                      {contentType === "image" &&
                                        item.imageUrl && (
                                          <img
                                            src={item.imageUrl}
                                            alt={item.imageAlt || ""}
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              objectFit: "cover",
                                              borderRadius: "4px",
                                              flexShrink: 0,
                                            }}
                                          />
                                        )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <p
                              className="qe-form__help"
                              style={{
                                fontSize: "11px",
                                marginTop: "8px",
                                marginBottom: "0",
                              }}
                            >
                              {t("answerKey.orderList.instruction")}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  })}
                </section>
              )}
            </>
          )}

          <footer className="qe-modal__footer">
            <div />
            <div className="qe-modal__actions">
              <button
                className="qe-button qe-button--ghost"
                type="button"
                onClick={onClose}
              >
                {t("answerKey.cancel")}
              </button>
              <button className="qe-button qe-button--primary" type="submit">
                {t("answerKey.save")}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default AnswerKeyModal;
